import { EventEmitter, Injectable } from '@angular/core';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import {
  DossierGQL,
  EchoGQL,
  MyEventGQL,
  PollGQL,
  RegisterPollingGQL,
  UnregisterKeyGQL,
} from '@sc/data-access-graphql';
import { StorageService } from '@sc/data-access-storage';
import { map } from 'rxjs/operators';
import { InsightService } from '../insight';
import {
  CloseDossier,
  DepartingDossier,
  Error,
  FreeSlotsResult,
  LastPing,
  NewDossier,
  OpenDossier,
  UpdatedDossier,
} from './models';

@Injectable({
  providedIn: 'root',
})
export class EventSubHttpService {
  private globalClientKey = this.generateRandomString(8);

  private readonly myEventGQLSubs: Map<string, Subscription> = new Map<
    string,
    Subscription
  >();
  public onEchoMessage = new EventEmitter<any>();
  public onErrors = new EventEmitter<Error[]>();
  public onFreeSlotsResults = new EventEmitter<FreeSlotsResult>();
  public onNewDossier = new EventEmitter<NewDossier>();
  public onUpdatedDossier = new EventEmitter<UpdatedDossier>();
  public onDepartingDossier = new EventEmitter<DepartingDossier>();
  public onOpenDossier = new EventEmitter<OpenDossier>();
  public onCloseDossier = new EventEmitter<CloseDossier>();
  public onLastPing = new EventEmitter<LastPing>();

  public isAuth = true;
  private onGoing = false;
  private currentSubscription = {};
  private pingInterval = {};
  private echoMessage = {};

  constructor(
    private readonly myEventGQL: MyEventGQL,
    private readonly registerPollingGQL: RegisterPollingGQL,
    private readonly unregisterKeyGQL: UnregisterKeyGQL,
    private readonly pollGQL: PollGQL,
    private readonly echoGQL: EchoGQL,
    private readonly dossierGQL: DossierGQL,
    private readonly insightService: InsightService,
    private readonly storage: StorageService
  ) {
    // Subscribe to OnEchoMessage
    this.subscribeToEchoMessage().subscribe((echo) => {
      let clientkey = echo.message;
      if (clientkey.indexOf('#') >= 0) {
        clientkey = clientkey.substring(0, clientkey.indexOf('#'));
      }
      this.echoMessage[clientkey] = true;
    });
  }

  getClientKey() {
    return this.globalClientKey;
  }

  subscribeToEchoMessage(): Observable<any> {
    return this.onEchoMessage;
  }

  async waitReadyness(): Promise<boolean> {
    return new Promise(async (resolve) => {
      let retry = 0;
      // Wait maximum for 10s
      while (!this.echoMessage[this.globalClientKey] && retry < 20) {
        await this.timeout(500);
        retry++;
      }
      resolve(this.echoMessage[this.globalClientKey]);
    });
  }

  subscribeToFreeSlotsResults(): Observable<FreeSlotsResult> {
    return this.onFreeSlotsResults;
  }

  subscribeToNewDossier(): Observable<NewDossier> {
    return this.onNewDossier;
  }

  subscribeToUpdatedDossier(): Observable<UpdatedDossier> {
    return this.onUpdatedDossier;
  }

  subscribeToDepartingDossier(): Observable<DepartingDossier> {
    return this.onDepartingDossier;
  }

  subscribeToOpenDossier(): Observable<OpenDossier> {
    return this.onOpenDossier;
  }

  subscribeToCloseDossier(): Observable<CloseDossier> {
    return this.onCloseDossier;
  }

  subscribeToErrors(): Observable<Error[]> {
    return this.onErrors;
  }

  subscribeToLastPing(): Observable<LastPing> {
    return this.onLastPing;
  }

  private receive(clientId: string, event: any) {
    // This method dispatch each message on dedicated eventEmitter based on the content
    if (event.echoResponse) {
      this.onEchoMessage.emit(event.echoResponse as string);
    }

    if (event.freeSlotsResults) {
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http receive freeSlotsResults from subscription',
        properties: { clientKey: clientId },
      });
      this.onFreeSlotsResults.emit(
        event.freeSlotsResults as unknown as FreeSlotsResult
      );
    }

    if (event.newDossier) {
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http receive newDossier from subscription',
        properties: { clientKey: clientId },
      });
      this.dossierGQL
        .fetch({ id: event.newDossier.dossierId }, { fetchPolicy: 'no-cache' })
        .subscribe((response) => {
          if (response.data.dossier) {
            this.onNewDossier.emit({
              dossier: response.data.dossier,
            } as unknown as NewDossier);
          } else throw new Error('Dossier not found');
        });
    }

    if (event.updatedDossier) {
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http receive updatedDossier from subscription',
        properties: { clientKey: clientId },
      });
      this.dossierGQL
        .fetch(
          { id: event.updatedDossier.dossierId },
          { fetchPolicy: 'no-cache' }
        )
        .subscribe((response) => {
          if (response.data.dossier) {
            this.onUpdatedDossier.emit({
              dossier: response.data.dossier,
              updateType: event.updatedDossier.updateType,
            } as unknown as UpdatedDossier);
          } else throw new Error('Dossier not found');
        });
    }

    if (event.departingDossier) {
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http receive departingDossier from subscription',
        properties: { clientKey: clientId },
      });
      this.onDepartingDossier.emit(
        event.departingDossier as unknown as DepartingDossier
      );
    }

    if (event.openDossier) {
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http receive openDossier from subscription',
        properties: { clientKey: clientId },
      });
      this.dossierGQL
        .fetch({ id: event.openDossier.dossierId }, { fetchPolicy: 'no-cache' })
        .subscribe((response) => {
          if (response.data.dossier) {
            this.onOpenDossier.emit({
              dossier: response.data.dossier,
            } as unknown as OpenDossier);
          } else throw new Error('Dossier not found');
        });
    }

    if (event.closeDossier) {
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http receive closeDossier from subscription',
        properties: { clientKey: clientId },
      });
      this.dossierGQL
        .fetch(
          { id: event.closeDossier.dossierId },
          { fetchPolicy: 'no-cache' }
        )
        .subscribe((response) => {
          if (response.data.dossier) {
            this.onCloseDossier.emit({
              dossier: response.data.dossier,
            } as unknown as CloseDossier);
          } else throw new Error('Dossier not found');
        });
    }

    if (event.errors) {
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http receive errors from subscription',
        properties: { clientKey: clientId },
      });
      this.onErrors.emit(event.errors as unknown as Error[]);
    }
    // Other event emmiters by checking data inside response.data ...
  }

  /*
   * Launch the default subscription system by using a graphgql subscription by websocket
   */
  private subscribe(clientId) {
    console.log(`EventSubHttpService sub client_id : ${clientId}`);
    if (!this.myEventGQLSubs?.get(clientId)) {
      this.myEventGQLSubs.set(
        clientId,
        this.myEventGQL
          .subscribe({ clientKey: clientId })
          .subscribe((response) => {
            this.receive(clientId, response.data.myEvent);
          })
      );
    }
  }

  /*
   * This method test and starts if you need the subscription system.
   * 1. test if the subscription is already operational by checking an echo message
   * 2. If the first echo test is ko, it launches subscription (by websocket)
   * 3. After waiting for 3 seconds, it tests a second time the echo message
   * 4. If the second echo doesn't work, then fallback to polling system
   *  (Reason : Some VPN or enterprise proxy doesn't allow websocket)
   */
  public async checkSubscription(subject?: string): Promise<boolean> {
    this.onGoing = true;
    let clientId;
    if (!subject) {
      clientId = this.globalClientKey;
      subject = clientId;
      const oldClientKey = this.storage.getItem('client-key', sessionStorage);
      if (oldClientKey && clientId !== oldClientKey)
        this.unsubscribe(oldClientKey);
      this.storage.setItem('client-key', this.globalClientKey, sessionStorage);
    } else {
      clientId = this.globalClientKey + '#' + subject;
      const oldClientKey = this.storage.getItem('client-key', sessionStorage);
      if (oldClientKey && this.globalClientKey !== oldClientKey)
        this.unsubscribe(oldClientKey + '#');
      this.storage.setItem('client-key', this.globalClientKey, sessionStorage);
    }

    if (!this.currentSubscription[clientId]) {
      this.currentSubscription[clientId] = true;
      // Subscription with Web Socket
      try {
        this.subscribe(clientId);
      } catch (error) {
        console.log('WebSocket subscription failed');
      }
      // wait 2s
      await this.timeout(2000);
      // Test Echo message
      const echoOK = await this.checkEchoMessage(subject);
      if (!echoOK) {
        // Si KO
        // Subscription by Long Polling
        this.subscribeByPolling(clientId);
        await this.timeout(1000);
        await this.checkEchoMessage(subject); // in order to get a first echo response (for await)
        // close the weSocket
        this.myEventGQLSubs.get(clientId)?.unsubscribe();
        this.myEventGQLSubs.delete(clientId);
      }
      this.onGoing = false;
      //ping every 5min if need
      if (!this.pingInterval[subject]) {
        try {
          if (this.currentSubscription[clientId]) {
            const eventSubService = this;
            await this.timeout(60 * 1000);
            this.pingInterval[subject] = setInterval(async function () {
              if (eventSubService.currentSubscription[clientId]) {
                const pingOK = await eventSubService.checkEchoMessage(subject);
                eventSubService.onLastPing.emit({
                  ok: pingOK,
                  date: new Date(),
                });
                if (!pingOK) {
                  console.log('PING KO -> relaunch subscription');
                  clearInterval(eventSubService.pingInterval[subject]);
                  delete eventSubService.pingInterval[subject];
                  delete eventSubService.currentSubscription[clientId];
                  eventSubService.globalClientKey =
                    eventSubService.generateRandomString(8);
                  eventSubService.checkSubscription(
                    clientId !== subject ? subject : null
                  );
                } else {
                  console.log('Ping ok');
                }
              }
            }, 1 * 60 * 1000);
          }
        } catch (error) {
          console.log('PB Ping ', error);
        }
      }
      return true;
    } else if (!this.onGoing) {
      const echoOK = await this.checkEchoMessage(subject);
      if (echoOK) {
        return true;
      } else {
        console.log('PING KO -> relaunch subscription');
        clearInterval(this.pingInterval[subject]);
        delete this.pingInterval[subject];
        delete this.currentSubscription[clientId];
        this.globalClientKey = this.generateRandomString(8);
        this.checkSubscription(clientId == subject ? null : subject);
        return false; // retrun false -> meaning : the subscriptionwas bad and relaunched
      }
    } else {
      // onGoing
      await this.timeout(1000); // wait 1 s
      if (!this.currentSubscription[clientId]) {
        return this.checkSubscription(clientId == subject ? null : subject);
      } else {
        return true;
      }
    }
  }

  private async checkEchoMessage(subject: string): Promise<boolean> {
    try {
      // Launch Echo Test message to failBack to long polling if need
      //console.log('Launch Echo Test for '+subject);
      this.echoMessage[subject] = false;

      // send an echo message with timestamp (in order to avoid graphql cached response)
      //console.log('checkSubscription send echo MESSAGE ...');
      await this.sendEchoMessage(subject);
      //console.log('sendEchoack ', ack.data.echo);

      // wait maximum 3s
      const now = new Date().getTime();
      let retry = 0;
      while (!this.echoMessage[subject] && retry < 15) {
        await this.timeout(200);
        retry++;
      }

      console.log(
        'checkEchoMessage end (' +
          (new Date().getTime() - now) +
          'ms): sub=' +
          subject +
          ' res=',
        this.echoMessage[subject]
      );
      return this.echoMessage[subject];
    } catch (error) {
      return false;
    }
  }

  private async sendEchoMessage(subject: string): Promise<boolean> {
    let envoiOk = false;
    // send an echo message with timestamp (in order to avoid graphql cached response)
    //console.log('checkSubscription send echo MESSAGE ...');
    try {
      const echoGQL$ = this.echoGQL
        .fetch(
          {
            clientKey: subject,
            message: subject + '#TESTSUB' + new Date().getTime(),
          },
          { fetchPolicy: 'no-cache' }
        )
        .pipe(take(1));

      await lastValueFrom(echoGQL$)
        .then(function (value) {
          envoiOk = value.data?.echo != null;
        })
        .catch(function (error) {
          console.log('ack echo error ' + JSON.stringify(error));
          envoiOk = false;
        });
    } catch (error) {
      console.log('error du echo');
      envoiOk = false;
    }

    return envoiOk;
  }

  private async timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private generateRandomString(length: number): string {
    const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const values = new Uint8Array(length);
    crypto.getRandomValues(values);

    return Array.from(values, (v) => charset[v % charset.length]).join('');
  }

  public setIsAuth(isauth: boolean) {
    this.isAuth = isauth;
    if (!isauth) {
      this.currentSubscription = {};
      Object.keys(this.pingInterval).forEach((subject) => {
        clearInterval(this.pingInterval[subject]);
        delete this.pingInterval[subject];
        console.log('stop ping interval for ' + subject);
      });
      for (const k of this.myEventGQLSubs.keys()) {
        this.myEventGQLSubs.get(k).unsubscribe();
        this.myEventGQLSubs.delete(k);
        console.log('stop Websocket  for ' + k);
      }
    }
  }

  private subscribeByPolling(clientId: string) {
    // Failover to longPolling
    console.log('Start long polling ...');
    this.insightService.trackEvent({
      name: 'FrontApp eventsub-http FailBack to long polling after websocket subscription exception',
      properties: { clientKey: clientId },
    });

    this.registerPollingGQL
      .fetch({ clientKey: clientId }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((response) => {
          console.log(
            'RegisteringPolling Response',
            response.data.registerPolling
          );
          return response.data.registerPolling;
        })
      )
      .subscribe(async (rp: any) => {
        if (rp.clientKey) {
          // The Polling registration is ok, waiting for event by cycle poll request.
          while (this.currentSubscription[clientId]) {
            if (this.isAuth) {
              try {
                const pollGql$ = this.pollGQL.mutate({ clientKey: clientId });

                const response = await lastValueFrom(pollGql$);
                if (response.data.poll)
                  this.receive(clientId, response.data.poll);
              } catch (e) {
                console.log('GraphQL Poll Error : ' + e.message);
                if (e.message.indexOf('401') > -1) {
                  this.isAuth = false;
                }
                await new Promise((resolve) => setTimeout(resolve, 1000));
              }
            } else {
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }
          }
        }
      });
  }

  unsubscribe(clientId?: string) {
    // destroy dedicated subscription by webSocket and also launch a unregisterKeyGQL mutation in order to clean the server side.
    // both are done because if there is polling it's important to do the unregisterKeyGQL
    // but in some case the unregisterKeyGQL will do nothing bacause unsub on websocket will have done the cleaning.
    if (!clientId) {
      // unsubscribe the current ClientKey
      // use when you logout from b2b
      clientId = this.globalClientKey;
      this.storage.removeItem('client-key', sessionStorage);
    }
    if (clientId === '#') {
      // unsubscribe all Subject of the current ClientKey
      // use when you logout from agenda
      clientId = this.globalClientKey + '#';
      this.storage.removeItem('client-key', sessionStorage);
    }
    if (clientId.indexOf('#') == clientId.length - 1) {
      // unsub with sharp at the end = unsub all subjects of the clientKey
      // use when you logout from agenda
      console.log(
        'Unsubscribe all subjects of this subscription key ...',
        clientId
      );
      this.insightService.trackEvent({
        name: 'FrontApp eventsub-http Unsubscribe all subjects of this subscription key',
        properties: { clientKey: clientId },
      });
      // Stop websocket if exist
      for (const k of this.myEventGQLSubs.keys()) {
        if (k.startsWith(clientId)) {
          this.myEventGQLSubs.get(k).unsubscribe();
          this.myEventGQLSubs.delete(k);
        }
      }
      // remove from current subscription
      for (const k in this.currentSubscription) {
        if (k.startsWith(clientId)) {
          delete this.currentSubscription[k];
        }
      }
    } else {
      if (clientId.startsWith('#')) {
        // unsub with sharp at the begining = unsub this subject for this global clientKey
        // use when you unselect a fititng station on agenda
        clientId = this.globalClientKey + clientId;
        console.log('Unsubscribe this subject ...', clientId);
        this.insightService.trackEvent({
          name: 'FrontApp eventsub-http Unsubscribe this subject',
          properties: { clientKey: clientId },
        });
      } else {
        // unsub a key (without subjet separated by #)
        // used when you refresh a b2C or b2b app.(old key on session storage)
        console.log('Unsubscribe old subscription key ...', clientId);
        this.insightService.trackEvent({
          name: 'FrontApp eventsub-http Unsubscribe old subscription key',
          properties: { clientKey: clientId },
        });
      }
      if (this.myEventGQLSubs?.has(clientId)) {
        this.myEventGQLSubs.get(clientId)?.unsubscribe();
        this.myEventGQLSubs.delete(clientId);
      }
      if (this.currentSubscription[clientId]) {
        delete this.currentSubscription[clientId];
      }
    }

    if (this.isAuth) {
      // launch the Unsubscribe mutation
      this.unregisterKeyGQL
        .mutate({ clientKey: clientId })
        .pipe(
          map((response) => {
            return response.data.unregisterKey;
          })
        )
        .subscribe(async (rp: any) => {
          if (rp.clientKey) {
            console.log('UnregisterKey Response', rp.clientKey);
          }
        });
    }
  }
}
